import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../config/firebase";
import { User, RegisterFormData } from "../types";

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  register: (data: RegisterFormData) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  resendVerificationEmailToUnverified: (
    email: string,
    password: string,
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(
    async (uid: string): Promise<User | null> => {
      try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (userDoc.exists()) {
          return userDoc.data() as User;
        }
        return null;
      } catch (error) {
        console.error("Error fetching user profile:", error);
        return null;
      }
    },
    [],
  );

  const refreshUserProfile = useCallback(async () => {
    if (currentUser) {
      const profile = await fetchUserProfile(currentUser.uid);
      setUserProfile(profile);
    }
  }, [currentUser, fetchUserProfile]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // If user exists but email is not verified, sign them out
      if (user && !user.emailVerified) {
        console.log("User email not verified, signing out");
        await signOut(auth);
        setCurrentUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      if (user) {
        const profile = await fetchUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserProfile]);

  const register = async (data: RegisterFormData) => {
    const { email, password, confirmEmail, confirmPassword, ...profileData } =
      data;

    // Validate email confirmation
    if (email !== confirmEmail) {
      throw new Error("EMAIL_MISMATCH");
    }

    // Validate password confirmation
    if (password !== confirmPassword) {
      throw new Error("PASSWORD_MISMATCH");
    }

    // Validate password strength (12+ chars, uppercase, lowercase, number, symbol)
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\/~`]).{12,}$/;
    if (!passwordRegex.test(password)) {
      throw new Error("WEAK_PASSWORD");
    }

    // Call backend validation before creating user
    const validateRegistrationFn = httpsCallable(
      functions,
      "validateRegistrationData",
    );
    await validateRegistrationFn({
      email,
      confirmEmail,
      password,
      confirmPassword,
    });

    // Create user with Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    // Create user profile in Firestore via Cloud Function
    const createUserProfileFn = httpsCallable(functions, "createUserProfile");
    await createUserProfileFn({
      uid: user.uid,
      nome: profileData.nome,
      cognome: profileData.cognome,
      email: email,
      cellulare: profileData.cellulare,
    });

    // Send email verification via SendGrid
    const sendEmailVerificationCustom = httpsCallable(
      functions,
      "sendEmailVerificationCustom",
    );
    await sendEmailVerificationCustom({
      email,
      origin: window.location.origin,
    });

    // Sign out the user - they must verify email before logging in
    await signOut(auth);
    setUserProfile(null);
  };

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    // Check if email is verified
    if (!userCredential.user.emailVerified) {
      // Sign out the user since they haven't verified their email
      await signOut(auth);
      throw new Error("EMAIL_NOT_VERIFIED");
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
  };

  const resetPassword = async (email: string) => {
    // Use custom SendGrid email function
    const sendPasswordResetEmailCustom = httpsCallable(
      functions,
      "sendPasswordResetEmailCustom",
    );
    await sendPasswordResetEmailCustom({
      email,
      origin: window.location.origin,
    });
  };

  const resendVerificationEmail = async () => {
    if (currentUser && !currentUser.emailVerified) {
      // Use custom SendGrid email function
      const sendEmailVerificationCustom = httpsCallable(
        functions,
        "sendEmailVerificationCustom",
      );
      await sendEmailVerificationCustom({
        email: currentUser.email,
        origin: window.location.origin,
      });
    }
  };

  // Function to resend verification email to an unverified user during login
  const resendVerificationEmailToUnverified = async (
    email: string,
    password: string,
  ) => {
    // First verify credentials are correct by signing in
    await signInWithEmailAndPassword(auth, email, password);

    // Use custom SendGrid email function
    const sendEmailVerificationCustom = httpsCallable(
      functions,
      "sendEmailVerificationCustom",
    );
    await sendEmailVerificationCustom({
      email,
      origin: window.location.origin,
    });

    await signOut(auth);
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    register,
    login,
    logout,
    resetPassword,
    refreshUserProfile,
    resendVerificationEmail,
    resendVerificationEmailToUnverified,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
